// IDA chat composer mic button.
//
// Owns the user-facing surface for voice input: button toggle, recording
// indicator (pulsing red dot), error messaging, privacy consent persistence,
// keyboard shortcut (Ctrl/Cmd+Shift+M), and forwards transcript updates to
// the composer's `inputValue` state.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/joy";
import MicNoneIcon from "@mui/icons-material/MicNone";
import MicIcon from "@mui/icons-material/Mic";

import { VoiceInputPrivacyModal } from "./VoiceInputPrivacyModal";
import { useVoiceRecording } from "./useVoiceRecording";
import { isVoiceInputSupported } from "./voiceInputUtils";

const CONSENT_STORAGE_KEY = "voice_input_consent_v1";

interface VoiceInputButtonProps {
  workspaceId: string;
  disabled?: boolean;
  onTranscriptUpdate: (transcript: string) => void;
  onAutoSubmit: (transcript: string) => void;
  userId?: string | null;
}

function consentKeyFor(userId?: string | null): string {
  return userId ? `${CONSENT_STORAGE_KEY}:${userId}` : CONSENT_STORAGE_KEY;
}

function hasConsent(userId?: string | null): boolean {
  try {
    return window.localStorage.getItem(consentKeyFor(userId)) === "true";
  } catch {
    return false;
  }
}

function persistConsent(userId?: string | null): void {
  try {
    window.localStorage.setItem(consentKeyFor(userId), "true");
  } catch {
    // Private-browsing or storage-full: silently accept this session only.
  }
}

export const VoiceInputButton: React.FC<VoiceInputButtonProps> = ({
  workspaceId,
  disabled = false,
  onTranscriptUpdate,
  onAutoSubmit,
  userId = null,
}) => {
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const supported = useMemo(() => isVoiceInputSupported(), []);

  const { state, error, start, stop, cancel, partialTranscript } =
    useVoiceRecording({
      workspaceId,
      onSubmit: onAutoSubmit,
      onPartial: onTranscriptUpdate,
    });

  const isRecording = state === "recording" || state === "requesting";

  const beginRecording = useCallback(async () => {
    if (!hasConsent(userId)) {
      setPrivacyOpen(true);
      return;
    }
    await start();
  }, [start, userId]);

  const handleClick = useCallback(() => {
    if (disabled || !supported) return;
    if (isRecording) {
      // Clicking the mic while recording STOPS and SENDS the transcript.
      // Discarding is Esc only.
      stop();
      return;
    }
    void beginRecording();
  }, [beginRecording, disabled, isRecording, stop, supported]);

  const handleConsentAccept = useCallback(() => {
    persistConsent(userId);
    setPrivacyOpen(false);
    void start();
  }, [start, userId]);

  const handleConsentDecline = useCallback(() => {
    setPrivacyOpen(false);
  }, []);

  // Global Ctrl/Cmd+Shift+M shortcut. Escape cancels an in-flight recording.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isToggle =
        (e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "m";
      if (isToggle && supported && !disabled) {
        e.preventDefault();
        handleClick();
        return;
      }
      if (e.key === "Escape" && isRecording) {
        cancel();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [cancel, disabled, handleClick, isRecording, supported]);

  // Surface any error to the parent textarea via aria-live; the textarea itself
  // also reflects the partialTranscript through onTranscriptUpdate.
  const ariaLabel = useMemo(() => {
    if (!supported) return "Voice input unavailable in this browser";
    if (error) return `Voice input error: ${error.message}`;
    switch (state) {
      case "recording":
        return "Stop and send voice input (recording); Escape to cancel";
      case "requesting":
        return "Starting voice input";
      case "processing":
        return "Submitting voice input";
      default:
        return "Start voice input";
    }
  }, [error, state, supported]);

  const tooltipText = useMemo(() => {
    if (!supported) return "Voice input not supported in this browser";
    if (error) return error.message;
    if (isRecording) return "Click to stop and send  -  Esc to cancel";
    return "Start voice input  -  Ctrl/Cmd+Shift+M";
  }, [error, isRecording, supported]);

  const disableButton = disabled || !supported;
  // Visible context for screen readers when recording. partialTranscript is
  // already piped to the textarea so we don't echo it visually here.
  const liveRegion = isRecording ? (
    <Box
      component="span"
      sx={{
        position: "absolute",
        width: 1,
        height: 1,
        overflow: "hidden",
        clip: "rect(0 0 0 0)",
      }}
      aria-live="polite"
    >
      {partialTranscript ? `Transcribing: ${partialTranscript}` : "Listening"}
    </Box>
  ) : null;

  return (
    <>
      <Tooltip title={tooltipText} variant="soft">
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          <IconButton
            variant="plain"
            color={isRecording ? "danger" : "neutral"}
            onClick={handleClick}
            disabled={disableButton}
            aria-label={ariaLabel}
            aria-pressed={isRecording}
            data-testid="voice-input-button"
            sx={{
              transition: "color 120ms ease",
            }}
          >
            {isRecording ? <MicIcon /> : <MicNoneIcon />}
          </IconButton>
          {isRecording && (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                top: 6,
                right: 6,
                width: 8,
                height: 8,
                borderRadius: "50%",
                bgcolor: "danger.500",
                animation: "voice-input-pulse 1s ease-in-out infinite",
                "@keyframes voice-input-pulse": {
                  "0%": { opacity: 1, transform: "scale(1)" },
                  "50%": { opacity: 0.45, transform: "scale(1.45)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
              }}
            />
          )}
          {liveRegion}
        </Box>
      </Tooltip>
      {error && (
        <Typography
          level="body-xs"
          sx={{ color: "danger.500", ml: 1, alignSelf: "center" }}
          role="alert"
        >
          {error.message}
        </Typography>
      )}
      <VoiceInputPrivacyModal
        open={privacyOpen}
        onAccept={handleConsentAccept}
        onDecline={handleConsentDecline}
      />
    </>
  );
};
